import SelectComponent from "components/atoms/select/SelectComponent";
import type { CustomSelectProps } from "components/atoms/select/SelectComponent";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import {
  BasePagination,
  SORT_DIRECTION,
} from "api/interfaces/commonInterface.interface";
import { getUserTotalList } from "api/userAPI";
import useApiError from "hook/useApiError";
import { useAppSelector } from "redux/hooks";
import { useCallback, useMemo, useState } from "react";
import { useQuery } from "react-query";
import { checkEmailRegEx } from "utils/regEx";
import { UserDto } from "api/interfaces/userInterface.interface";
import styled from "styled-components";
import { calcRem } from "styles/theme";
import { toTrim } from "utils/functions";
import { Option } from "utils/options";
export interface UserEmailSelect {
  [key: string]: string;
}

export type EmailSelectComponentProps = Omit<
  CustomSelectProps<string[]>,
  "mode" | "options" | "value" | "defaultValue" | "onChange"
> & {
  value?: string[];
  defaultValue?: string[];
  onChange?: (value: string[], option: Option[]) => void;
  isVs?: boolean;
};

const optionRender = (option: any) => {
  const data = option?.data as any;
  return (
    <EmailOptionRenderWrapper>
      <EmailOptionLabel>{data?.label}</EmailOptionLabel>
      <EmailOptionValue>{data?.value}</EmailOptionValue>
    </EmailOptionRenderWrapper>
  );
};

export default function EmailSelectComponent(props: EmailSelectComponentProps) {
  const { isVs, onChange: onChangeProp, value, defaultValue, ...rest } = props;

  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const themeMode = useAppSelector((state) => state.theme.theme);
  const { lang, notifyError } = useApiError();
  const [uncontrolledValue, setUncontrolledValue] = useState<string[]>(
    defaultValue ?? []
  );

  const [userQueryInfo] = useState<BasePagination>({
    total: 0,
    pageNum: 0,
    pageLimit: 1000,
    keyword: "",
    sortType: "name",
    sortDirection: SORT_DIRECTION.ASC,
  });

  const { data, isFetching } = useQuery(
    ["user", userQueryInfo, selectedAccount.accountId],
    () =>
      getUserTotalList({
        payload: userQueryInfo,
        accountId: selectedAccount.accountId,
      }),
    {
      retry: 0,
      enabled: value === undefined || isVs,
      refetchOnWindowFocus: false,
    }
  );

  const options = useMemo(() => {
    if (data?.result) {
      const tempRes = (data.result || []).map((item: UserDto) => ({
        value: toTrim(item.email),
        label: toTrim(item.name),
      }));
      return tempRes;
    }
    return [];
  }, [data]);

  const resolvedValue = value ?? uncontrolledValue;

  const handleChange = useCallback(
    (nextValue: any) => {
      console.log(nextValue);
      const emails = (Array.isArray(nextValue) ? nextValue : [nextValue])
        .filter(Boolean)
        .map((email) => toTrim(email));

      const hasInvalidEmail = emails.some((email) => !checkEmailRegEx(email));

      if (hasInvalidEmail) {
        notifyError(lang.validateMsg.user.email);
        return;
      }

      if (value === undefined) {
        setUncontrolledValue(emails);
      }
      const optionValue = emails.map((email) => ({
        value: email,
        label:
          options.find((option: Option) => option.value === email)?.label ??
          email,
      })) as Option[];
      onChangeProp?.(emails, optionValue);
    },
    [lang.validateMsg.user.email, notifyError, onChangeProp, value, options]
  );

  return (
    <SelectComponent
      {...rest}
      mode="tags"
      options={options}
      value={resolvedValue}
      placeholder={
        props.placeholder ?? "Click to select user or enter email address"
      }
      onChange={handleChange}
      optionRender={optionRender}
      tokenSeparators={[",", " ", "\n", "\t"]}
      showSearch
      listHeight={300}
      virtual={false}
      className={themeMode === "light" ? "light-select" : "dark-select"}
      loading={isFetching}
    />
  );
}

const EmailOptionRenderWrapper = styled.div`
  padding: 5px;
  cursor: pointer;
`;

const EmailOptionLabel = styled.div`
  font-size: ${calcRem(14)};
  font-weight: 500;
`;

const EmailOptionValue = styled.div`
  font-size: ${calcRem(13)};
  font-weight: 400;
`;
